import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ErrorModal,
  InputTextVertical,
  SelectControllerVertical,
  SuccessModal,
  VerticalInputFormLayout,
} from "@components";

import { useSettingsOfficeById } from "@hooks/HRM/settings/useSettingsOfficeById";
import { useGeoData } from "@hooks/useGeoData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import useActionCreator from "@redux/hooks/useActionCreator";

import { getConvertedGeoData } from "@helpers/geoDataConvert";
import { getConvertedOfficeTypeData } from "@helpers/officeTypeConvert";

import {
  HRMSettingsOfficeFormDataType,
  HRMSettingsOfficeValidation,
} from "@validations/HRM/settings/validation";

import { FORM_FIELDS } from "@constants/form";
import { GEO_DATA } from "@constants/geo-data";
import { MASTER_DATA } from "@constants/master-data";

const { NAME, NAME_BN, ADDRESS, ADDRESS_BN, TYPE, DIVISION, DISTRICT } =
  FORM_FIELDS.HRM_SETTINGS.CREATE_OFFICE;
const { OFFICE_TYPE } = MASTER_DATA;
const { DISTRICTS, DIVISIONS } = GEO_DATA;

export default function OfficeAddressForm() {
  const [id, setId] = useState<number | string>("");
  const [successSubtitle, setSuccessSubtitle] = useState<string>("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: officeTypes } = useMasterData(OFFICE_TYPE);
  const { data: divisions } = useGeoData(DIVISIONS);
  const { data: districts } = useGeoData(DISTRICTS);
  const { officeState } = useSettingsOfficeById(false);
  const { getSingleData } = officeState;

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm<HRMSettingsOfficeFormDataType>({
    resolver: yupResolver(HRMSettingsOfficeValidation),
  });

  const divisionId = watch(DIVISION);

  const {
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  } = useCustomSelector((store) => store.hrm.settings.officeState);

  const { HRMActionsHandlers } = useActionCreator();
  const {
    settingsOfficeCreateActionHandler,
    settingsOfficeUpdateActionHandler,
  } = HRMActionsHandlers;

  const resetAction = () => {
    reset();
    setId("");
  };

  const createSuccessAction = () => {
    resetAction();
    setIsSuccessModalOpen(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<HRMSettingsOfficeFormDataType> = (
    data: any,
  ) => {
    if (id) {
      settingsOfficeUpdateActionHandler(id, data);
    } else {
      settingsOfficeCreateActionHandler(data);
    }
  };

  useEffect(() => {
    if (getSingleData) {
      setValue(NAME, getSingleData.name);
      setValue(NAME_BN, getSingleData.nameBn);
      setValue(ADDRESS, getSingleData.address);
      setValue(ADDRESS_BN, getSingleData.addressBn);
      setValue(TYPE, getSingleData.type?.id);
      setValue(DIVISION, getSingleData.division?.id);
      setValue(DISTRICT, getSingleData.district?.id);
      setId(getSingleData.id);
    }
  }, [getSingleData]);

  useEffect(() => resetAction(), []);

  useEffect(() => {
    if (createSuccess) {
      setSuccessSubtitle("You have created an office");
      setIsSuccessModalOpen(true);
    }

    if (updateSuccess) {
      setSuccessSubtitle("You have updated an office");
      setIsSuccessModalOpen(true);
    }

    if (deleteSuccess) {
      setSuccessSubtitle("You have deleted an office");
      setIsSuccessModalOpen(true);
    }

    if (createError || updateError || deleteError) {
      setSuccessSubtitle("");
      setIsErrorModalOpen(true);
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  ]);

  return (
    <>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle={successSubtitle}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}

      <VerticalInputFormLayout
        label={
          id
            ? t("HRM_SETTINGS.OFFICE_ADDRESS.FORM.LABEL_UPDATE")
            : t("HRM_SETTINGS.OFFICE_ADDRESS.FORM.LABEL")
        }
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isUpdate={id ? true : false}
        cancelAction={resetAction}
      >
        <InputTextVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.NAME"
          register={{ ...register(NAME) }}
          error={errors as any}
          required
          cols={4}
        />

        <InputTextVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.NAME_BN"
          register={{ ...register(NAME_BN) }}
          error={errors as any}
          required
          cols={4}
        />

        <InputTextVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.ADDRESS"
          register={{ ...register(ADDRESS) }}
          error={errors as any}
          required
          cols={4}
        />

        <InputTextVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.ADDRESS_BN"
          register={{ ...register(ADDRESS_BN) }}
          error={errors as any}
          required
          cols={4}
        />

        <SelectControllerVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.TYPE"
          name={TYPE}
          register={{
            ...register(TYPE),
          }}
          options={getConvertedOfficeTypeData(officeTypes, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={4}
        />

        <SelectControllerVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.DIVISION"
          name={DIVISION}
          register={{
            ...register(DIVISION),
          }}
          options={getConvertedGeoData(divisions, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={4}
        />

        <SelectControllerVertical
          label="HRM_SETTINGS.OFFICE_ADDRESS.FORM.FIELDS.DISTRICT"
          name={DISTRICT}
          register={{
            ...register(DISTRICT),
          }}
          options={getConvertedGeoData(
            districts.filter(
              (district: any) => district.divisionId === divisionId,
            ),
            language,
          )}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={4}
        />
      </VerticalInputFormLayout>
    </>
  );
}
